/**
 * simulator/api/axiosClient.js
 * Axios híbrido para el simulador:
 *  - Intercepta llamadas de solo-lectura (services, workers, events, contactos)
 *    y devuelve datos del estado en memoria.
 *  - Pasa al backend REAL todas las llamadas de booking (/peluqueria/appointment),
 *    contactos, búsquedas y cualquier ruta no manejada en memoria.
 *  - Al crear una cita real en el backend, también despacha ADD_APPOINTMENT al
 *    estado local para que el calendario la muestre inmediatamente.
 */

import realAxiosClient from "../../api/axiosClient"; // cliente real de ventas (con token + backend URL)
import dayjs from "dayjs";
import { getState, dispatch } from "../store";
import { getSlotInterval } from "../slotIntervalStore";
import { getMockData, SECTOR_DATA_MAP, getContactsForSector } from "../mockData";
import { isAppointmentSector } from "../constants/appointmentSectors";
import { getVendorDemoPhone, DAVID_ROMERO_PHONE } from "../utils/vendorPhoneMap";
import { shouldFakeAvailability } from "./availabilityGate";
import { getProfesoresList, isProfesorId, getProfesorSchedule, setProfesorSchedule } from "../pages/autoescuela/profesoresStore";
import { citasVisibles } from "../trabajadoresDemo";

// Autoescuela: los trabajadores son los profesores del simulador (profesoresStore), no `state.workers`.
const isAutoescuelaState = (state) => String(state?.sector || "").toLowerCase() === "autoescuela";

// Capitaliza CADA palabra ("Marta Peris"), no solo la primera: con
// `.slice(1).toLowerCase()` el apellido de un trabajador salía en minúscula
// ("Marta peris") en las cabeceras del calendario y en el selector de
// especialista, que es lo primero que se ve en una demo.
function capitalizeWords(raw) {
  return String(raw || "")
    .split(/(\s+)/)
    .map((part) => (/^\s+$/.test(part) || !part ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
    .join("");
}

function parseVehiclesSim(raw) {
  let v = raw;
  if (v && v.S !== undefined) v = v.S;
  if (typeof v === "string") { try { v = JSON.parse(v); } catch { v = []; } }
  return Array.isArray(v) ? v : [];
}

// Cache in-memory de asignaciones servicio→trabajadores para el simulador.
// Cuando el componente hace PUT /peluqueria/service-assignments, guardamos el
// body aquí para que el siguiente GET devuelva exactamente lo asignado (sin
// depender de state.services del store, que en lavadero by_service no contiene
// los service_ids correctos).
let _cachedServiceAssignments = null;
// Persistencia en memoria de horarios + periodos especiales por trabajador,
// para demostrar el round-trip PUT→GET de "Horarios especiales" sin backend.
// Keyed por target_user_id.
let _mockSchedulesByUser = {};

const respond = (data) => Promise.resolve(data);

// Templates creadas en sesión — persisten hasta reload o salir del simulator
const _sessionTemplates = [];

// Devuelve el mapa display→real en tiempo de ejecución (el teléfono del vendedor
// logueado puede cambiar entre sesiones, así que no se puede cachear en módulo).
function getDemoPhoneMap() {
  const vendorPhone = getVendorDemoPhone();
  return {
    [vendorPhone]: vendorPhone,           // Javier Jiménez + Carmen García
    [DAVID_ROMERO_PHONE]: DAVID_ROMERO_PHONE, // David Romero (número fijo)
  };
}

/* ─── Helpers para disponibilidad simulada (trabajadores mock) ── */
/* isMockWorkerId / shouldFakeAvailability viven en ./availabilityGate */

// Genera días hábiles disponibles para los próximos N días
const generateFakeAvailableDays = (daysAheadInput = 30) => {
  const days = [];
  const closedDays = [];
  const fullDays = [];
  const today = new Date();

  // Asegurar que daysAhead es un número válido y razonable
  let daysAhead = 30;
  if (typeof daysAheadInput === "number") {
    daysAhead = daysAheadInput;
  } else if (typeof daysAheadInput === "string") {
    const parsed = parseInt(daysAheadInput);
    if (!isNaN(parsed)) daysAhead = parsed;
  }

  let workdayIndex = 0;
  for (let i = 1; i <= daysAhead; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dow = d.getDay();
    // Excluir domingos (0). Incluir sábados (6) y el resto del día de semana.
    if (dow !== 0) {
      workdayIndex++;
      // Demo: marcar un día laborable como cerrado por festivo (espejo del backend,
      // que devuelve closed_days con closed:true + motivo). Sirve para mostrar el día
      // "Cerrado" en el grid de reserva sin que la semana desaparezca.
      if (workdayIndex === 4) {
        closedDays.push({
          date: d.toISOString().split("T")[0],
          total_slots: 0,
          morning_slots: 0,
          afternoon_slots: 0,
          closed: true,
          closed_reason: "Cerrado: Festivo local",
        });
        continue;
      }
      // Demo: primer día laborable "sin huecos" (lleno/sin bloque suficiente para la
      // duración). Espejo de full_days del backend: se marca en gris "Sin huecos".
      if (workdayIndex === 1) {
        fullDays.push({
          date: d.toISOString().split("T")[0],
          total_slots: 0,
          morning_slots: 0,
          afternoon_slots: 0,
          full: true,
        });
        continue;
      }
      const total_slots = Math.min((workdayIndex - 1) * 9 + 1, 36);
      const morning_slots = Math.round(total_slots * 0.6);
      const afternoon_slots = total_slots - morning_slots;
      days.push({
        date: d.toISOString().split("T")[0],
        total_slots,
        morning_slots,
        afternoon_slots,
      });
    }
  }
  return { available_days: days, closed_days: closedDays, full_days: fullDays };
};

// Genera slots de hora disponibles para los trabajadores mock, respetando el
// intervalo configurado en el simulador (slotIntervalStore).
const generateFakeSlots = (workers, { withUnavailable = false } = {}) => {
  const step = getSlotInterval();
  const windows = [
    [9 * 60, 13 * 60], // mañana 09:00–13:00
    [16 * 60, 20 * 60], // tarde 16:00–20:00
  ];
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
  const slots = [];
  for (const [start, end] of windows) {
    for (let m = start; m < end; m += step) {
      slots.push(fmt(m));
    }
  }
  // Ventanas de horario configuradas (para mostrarlas en el paso de hora).
  const scheduleWindows = windows.map(([start, end]) => ({
    start: fmt(start),
    end: fmt(end),
  }));
  const list = workers.map((w) => ({
    worker_id: w.worker_id || w.user_id || w.id,
    name: w.name || w.displayName || "",
    available_slots: slots,
    schedule_windows: scheduleWindows,
  }));

  // Espejo del caso real: mostramos el último trabajador como NO disponible por
  // no tener el servicio asignado, para demostrar la explicación en la UI.
  if (!withUnavailable || list.length <= 1) {
    return { workers: list, unavailable_workers: [] };
  }
  const available = list.slice(0, -1);
  const last = list[list.length - 1];
  return {
    workers: available,
    unavailable_workers: [
      {
        worker_id: last.worker_id,
        name: last.name,
        reason: "not_assigned",
        schedule_windows: [],
      },
    ],
  };
};

/* ─── Helpers de URL para intercepciones in-memory ─────────── */

// Servicios: /peluqueria/services  o  /services
const isServicesUrl = (url) =>
  /^\/(peluqueria\/)?services(\?|$)/.test(url);

// Trabajadores: /peluqueria/workers  o  /workers
const isWorkersUrl = (url) =>
  /^\/(peluqueria\/)?workers(\?|$)/.test(url);

// Eventos de calendario: /peluqueria/events, /calendar/events — NOT /peluqueria/appointment
const isEventsUrl = (url) =>
  /^\/(peluqueria\/events|calendar\/events)(\?|$)/.test(url);

// Dos citas demo con anticipo (Stripe) para que las tarjetas "Anticipo
// pendiente/pagado" y el botón "Reembolsar anticipo" del EventModal se vean sin
// configurar nada. Solo en sectores de citas. 100% en memoria.
// Sectores SIN calendario de citas (comida/bar/autoescuela/marketing): en el resto
// (todos los de citas, incluidos alias como peluqueria_unisex) sí se inyectan las
// citas demo del anticipo. Gate NEGATIVO a propósito: un allowlist se quedaba corto
// (peluqueria_unisex no está en APPOINTMENT_SECTORS y el sector no veía las demos).
const NON_APPOINTMENT_DEMO_SECTORS = new Set([
  "comida_para_llevar",
  "distribucion",
  "pizzeria",
  "restaurantes",
  "restaurante",
  "bares_deportes",
  "bares_tematicos",
  "autoescuela",
  "marketing",
  "taller",
]);
const depositDemoEvents = (state) => {
  const demoSector = String(state?.sector || "").toLowerCase().trim();
  if (!demoSector || NON_APPOINTMENT_DEMO_SECTORS.has(demoSector)) return [];
  const sector = (state.sector || "barberia").toLowerCase();
  // «Peluquería» del selector es peluqueria_unisex: sin el alias salía «Corte Normal» de barbería.
  const sectorData = SECTOR_DATA_MAP[sector === "peluqueria_unisex" ? "peluquerias" : sector] || SECTOR_DATA_MAP.barberia;
  const svc = (sectorData.services || [])[0] || { name: "Servicio", price: 25, color: "#3B82F6" };
  const workerId =
    state.workers?.[0]?.worker_id || state.workers?.[0]?.user_id || null;
  const today = dayjs();
  const at = (h, m) =>
    today.hour(h).minute(m).second(0).millisecond(0).format("YYYY-MM-DDTHH:mm:ss");
  const svcName = svc.name || svc.nombre || "Servicio";
  const svcPrice = svc.price ?? svc.precio ?? 25;
  const svcColor = svc.color || "#6366F1";
  const shared = {
    service_name: svcName,
    service_price: svcPrice,
    service_duration: 30,
    color: svcColor,
    worker_id: workerId,
    origin_kind: "booking",
    creation_source: "whatsapp",
    deposit_amount: 10,
  };
  return [
    {
      ...shared,
      event_id: "demo-deposit-pending",
      id: "demo-deposit-pending",
      start: at(12, 0),
      end: at(12, 30),
      client_name: "Lucía (demo anticipo)",
      client_phone: "+34600000001",
      confirmed: false,
      payment_status: "pending",
    },
    {
      ...shared,
      event_id: "demo-deposit-paid",
      id: "demo-deposit-paid",
      start: at(13, 0),
      end: at(13, 30),
      client_name: "Marco (demo anticipo)",
      client_phone: "+34600000002",
      confirmed: true,
      payment_status: "paid",
      payment_intent_id: "pi_demo_123",
    },
    {
      ...shared,
      event_id: "demo-deposit-paid-2",
      id: "demo-deposit-paid-2",
      start: at(16, 30),
      end: at(17, 0),
      client_name: "Sara (demo anticipo)",
      client_phone: "+34600000003",
      confirmed: true,
      payment_status: "paid",
      payment_intent_id: "pi_demo_456",
      deposit_amount: 20,
    },
    {
      ...shared,
      event_id: "demo-deposit-refunded",
      id: "demo-deposit-refunded",
      start: at(17, 30),
      end: at(18, 0),
      client_name: "Iván (demo anticipo reembolsado)",
      client_phone: "+34600000004",
      confirmed: true,
      payment_status: "refunded",
      payment_intent_id: "pi_demo_789",
      refund_id: "re_demo_789",
    },
    // Un par el día siguiente para que también se vea en vista semana.
    {
      ...shared,
      event_id: "demo-deposit-paid-3",
      id: "demo-deposit-paid-3",
      start: dayjs(at(11, 0)).add(1, "day").format("YYYY-MM-DDTHH:mm:ss"),
      end: dayjs(at(11, 30)).add(1, "day").format("YYYY-MM-DDTHH:mm:ss"),
      client_name: "Nuria (demo anticipo)",
      client_phone: "+34600000005",
      confirmed: true,
      payment_status: "paid",
      payment_intent_id: "pi_demo_abc",
    },
    {
      ...shared,
      event_id: "demo-deposit-pending-2",
      id: "demo-deposit-pending-2",
      start: dayjs(at(12, 30)).add(1, "day").format("YYYY-MM-DDTHH:mm:ss"),
      end: dayjs(at(13, 0)).add(1, "day").format("YYYY-MM-DDTHH:mm:ss"),
      client_name: "Pau (demo anticipo)",
      client_phone: "+34600000006",
      confirmed: false,
      payment_status: "pending",
      deposit_amount: 15,
    },
  ];
};

// Contactos (listado completo): /contactos — pero NO /contactos/search ni /contactos/{id}
const isContactosListUrl = (url) =>
  /^\/contactos(\?|$)/.test(url);

/* ─── Servicios persistidos por lead (sectores de reserva de citas) ───────
 * Cuando el simulador se abre desde LeadEditModal con un lead cuyo sector es
 * de reserva de citas, las mutaciones sobre el catálogo de servicios
 * (/peluqueria/services*) se redirigen al backend reutilizado /ventas/lead-carta*
 * con `kind=service`, persistiendo el catálogo por contact_id en vez de tocar
 * la tabla global p_services de la empresa del comercial.
 */

const isLeadAppointmentMode = (state) =>
  !!state?.contactId && isAppointmentSector(state?.sector);

// Detecta `/peluqueria/services/{id}` o `/services/{id}` y devuelve el id.
const extractServiceId = (url) => {
  const m = url.match(/^\/(?:peluqueria\/)?services\/([^/?]+)/);
  return m ? m[1] : null;
};

// Heurística: los service_id "reales" de lead-carta son UUID v4. Los service_id
// provenientes de sugerencias de UnifiedServicesTable son slugs (`corte-de-pelo`).
// Al crear, si el id parece slug lo omitimos para que el Lambda genere un uuid.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuidLike = (id) => typeof id === "string" && UUID_RE.test(id);

const normalizeGender = (g) => {
  const v = (g || "").toString().toLowerCase();
  if (v === "caballero" || v === "caballeros") return "hombre";
  if (v === "senora" || v === "señora" || v === "damas" || v === "dama") return "mujer";
  if (v === "hombre" || v === "mujer" || v === "unisex") return v;
  return "unisex";
};

const matchesGenderBucket = (serviceGender, bucket) => {
  const g = (serviceGender || "unisex").toLowerCase();
  if (bucket === "hombre") return g === "hombre" || g === "caballero" || g === "caballeros";
  if (bucket === "mujer") return g === "mujer" || g === "senora" || g === "señora" || g === "damas";
  return g === bucket;
};

// Payload para POST /ventas/lead-carta/item con kind=service.
const toLeadCartaItem = (svc, { includeItemId = false } = {}) => {
  const duration = Number(svc?.total_duration || svc?.duration || 30) || 30;
  const rawPrice = svc?.price;
  const price =
    rawPrice === "" || rawPrice == null || Number.isNaN(Number(rawPrice))
      ? 0
      : Number(rawPrice);
  const item = {
    kind: "service",
    name: String(svc?.name || "").trim(),
    description: String(svc?.description || ""),
    duration,
    price,
    gender: normalizeGender(svc?.gender),
    color: svc?.color || "#3B82F6",
    available: svc?.enabled !== false && svc?.available !== false,
  };
  if (includeItemId && isUuidLike(svc?.service_id)) {
    item.item_id = svc.service_id;
  }
  return item;
};

// Aplica la forma de `state.services` a partir de un item devuelto por lead-carta.
const leadCartaItemToService = (it) => ({
  service_id: it.item_id || it.service_id,
  name: it.name || "",
  duration: it.duration || 30,
  total_duration: it.duration || 30,
  work_duration: it.duration || 30,
  processing_duration: 0,
  evaluation_duration: 15,
  gender: normalizeGender(it.gender),
  price: Number(it.price) || 0,
  color: it.color || "#3B82F6",
  description: it.description || "",
  enabled: it.available !== false,
});

/* ─── Cliente híbrido ───────────────────────────────────────── */

const axiosClient = {
  get: (url, config = {}) => {
    const state = getState();
    let params = config?.params || {};

    // Extraer params de la URL si vienen en el string (ej: /ruta?a=1&b=2)
    if (url.includes("?")) {
      const parts = url.split("?");
      const queryString = parts[1];
      const urlParams = Object.fromEntries(new URLSearchParams(queryString));
      params = { ...urlParams, ...params };
    }

    // ── In-memory: datos que gestionamos localmente ─────────
    if (isServicesUrl(url)) return respond(state.services || []);
    if (isWorkersUrl(url)) {
      // Normalizar workers al shape esperado por UnifiedServicesTable / ServiceAssignmentManager:
      // los mocks traen { name, email, ... } pero los consumidores filtran por `username`.
      const normalized = (state.workers || []).map((w) => {
        const rawName = w.name || w.username || w.displayName || "";
        const cleanName = rawName.includes("@") ? rawName.split("@")[0] : rawName;
        const formattedName =
          capitalizeWords(cleanName);
        return {
          ...w,
          user_id: w.worker_id || w.user_id || w.id,
          username: w.username || formattedName,
          full_name: w.full_name || formattedName,
          name: w.name || formattedName,
        };
      });
      return respond(normalized);
    }
    // Las citas de un trabajador quitado con «− N trabajadores +» no se ven (trabajadoresDemo.js).
    if (isEventsUrl(url))   return respond(citasVisibles([...depositDemoEvents(state), ...(state.appointments || [])], state.workers || []));
    if (isContactosListUrl(url)) return respond(state.contacts || []);

    // ── Food-orders routes (simulator: return empty responses — páginas mock son autónomas) ──
    if (url.startsWith("/pedidos")) return respond([]);
    if (url.startsWith("/carta")) return respond([]);
    if (url.startsWith("/reservas")) return respond([]);
    if (url.startsWith("/menu-dia")) return respond({ published: false, sections: [], pickup_hours: [] });

    // Autoescuela: /usuarios = profesores del simulador (el editor de horario saca de aquí el nombre).
    if ((url === "/usuarios" || url.startsWith("/usuarios?")) && isAutoescuelaState(state)) {
      return respond(
        getProfesoresList().map((i) => ({
          user_id: i.instructor_id,
          username: i.name,
          full_name: i.name,
          name: i.name,
          phone: i.phone || null,
          level: 1,
          department_id: "dept-default",
        })),
      );
    }

    // /usuarios → trabajadores formateados como usuarios (para ServiceAssignmentManager, UserScheduleForm)
    if (url === "/usuarios" || url.startsWith("/usuarios?")) {
      const workers = state.workers || [];
      return respond(
        workers.map((w) => {
          const rawName = w.name || w.username || w.displayName || "";
          // Limpiar si es un email
          const cleanName = rawName.includes("@") ? rawName.split("@")[0] : rawName;
          // Capitalizar
          const formattedName = capitalizeWords(cleanName);

          return {
            user_id: w.worker_id || w.user_id || w.id,
            username: formattedName,
            full_name: formattedName,
            name: formattedName,
            email: w.email || "",
            phone: w.phone || null, // Teléfono personal (para reabrir la edición ya relleno)
            level: w.level || 1,
            department_id: w.department_id || "dept-default",
          };
        }),
      );
    }

    // /peluqueria/service-assignments → si hay cache (PUT previo) devolverlo;
    // si no, generar default (todos los servicios × todos los trabajadores).
    if (url.includes("/peluqueria/service-assignments")) {
      if (Array.isArray(_cachedServiceAssignments)) {
        return respond(_cachedServiceAssignments);
      }
      const services = state.services || [];
      const workers  = state.workers  || [];
      const assignments = [];
      services.forEach((s) => {
        workers.forEach((w) => {
          assignments.push({
            service_id: s.service_id || s.id,
            user_id: w.worker_id || w.user_id || w.id,
          });
        });
      });
      return respond(assignments);
    }

    // Autoescuela: el horario del profesor vive en profesoresStore (lo lee también Planificación).
    if (url.includes("/peluqueria/user-schedule") && isAutoescuelaState(state) && isProfesorId(params.target_user_id)) {
      return respond(getProfesorSchedule(params.target_user_id));
    }

    // /peluqueria/user-schedule → horario + periodos especiales (persistidos en memoria)
    if (url.includes("/peluqueria/user-schedule")) {
      const uid = params.target_user_id || "__self__";
      const stored = _mockSchedulesByUser[uid];
      const workSlots = [
        { start: "10:00", end: "14:00" },
        { start: "16:00", end: "19:00" }
      ];
      const defaultSchedule = {
        lunes:       workSlots,
        martes:      workSlots,
        "miércoles": workSlots,
        jueves:      workSlots,
        viernes:     workSlots,
        sábado:      [{ start: "10:00", end: "14:00" }], // solo mañana
        domingo:     [],
      };
      return respond({
        schedule: stored?.schedule || defaultSchedule,
        schedule_overrides: stored?.schedule_overrides || [],
      });
    }

    // /departamentos → un departamento por defecto
    if (url.startsWith("/departamentos")) {
      return respond([{ dept_id: "dept-default", nombre: "General", users: [] }]);
    }

    // /subscription-plan → plan demo
    if (url.includes("/subscription-plan") || url.includes("/stripe")) {
      return respond({ plan: "demo", status: "active" });
    }

    // /company → datos de empresa mock
    if (url === "/company" || url.startsWith("/company?")) {
      return respond({ company_id: "simulator", name: "Demo Company" });
    }

    // /admin/sector-config → configuración de sector mock
    if (url.includes("/admin/sector-config")) {
      const lang = params.language || "es";
      
      // Detectar sector: puede venir de slug en query params o en el path
      let sectorSlug = "";
      const slugMatch = url.match(/slug=([^&]+)/);
      if (slugMatch) {
        sectorSlug = slugMatch[1];
      } else {
        // Extraer del path: /admin/sector-config/{sectorId}
        const pathMatch = url.match(/\/admin\/sector-config\/([^/?]+)/);
        if (pathMatch) {
          sectorSlug = pathMatch[1];
        }
      }
      
      // Si no hay slug y es la ruta base, devolver lista de sectores
      if (!sectorSlug && (url.endsWith("/admin/sector-config") || url.endsWith("/admin/sector-config/"))) {
        return respond({
          configs: Object.keys(SECTOR_DATA_MAP).map(key => ({
            sector: key,
            label: SECTOR_DATA_MAP[key].label || key
          }))
        });
      }

      // Normalizar sectorSlug para el lookup en SECTOR_DATA_MAP
      let lookupKey = (sectorSlug || state.sector || "barberia").toLowerCase();
      if (lookupKey.includes("peluquer")) lookupKey = "peluquerias";
      if (lookupKey.includes("barber")) lookupKey = "barberia";
      if (lookupKey.includes("estetica")) lookupKey = "estetica";
      if (lookupKey.includes("fisio")) lookupKey = "fisioterapia";

      // Obtener servicios desde mockData basado en el sector normalizado
      const sectorData = SECTOR_DATA_MAP[lookupKey] || SECTOR_DATA_MAP.barberia;
      const services = sectorData.services || [];
      
      const isHairBusiness = lookupKey === "peluquerias" || lookupKey === "barberia" || lookupKey === "estetica";
      
      // Si es negocio de peluquería/belleza, separar servicios por género para UnifiedServicesTable
      if (isHairBusiness && services.some(s => s.gender && s.gender !== "unisex")) {
        const hombre = services.filter(s => (s.gender || "").toLowerCase() === "hombre" || (s.gender || "").toLowerCase() === "caballero");
        const mujer = services.filter(s => (s.gender || "").toLowerCase() === "mujer" || (s.gender || "").toLowerCase() === "dama" || (s.gender || "").toLowerCase() === "señora");
        const unisex = services.filter(s => !s.gender || (s.gender || "").toLowerCase() === "unisex");
        
        return respond({
          config: {
            default_services: {
              [lang]: {
                hombre: hombre.map((s) => ({
                  nombre: s.name || s.nombre,
                  duracion_minutos: s.duration || s.duracion_minutos,
                  gender: s.gender || "unisex",
                })),
                mujer: mujer.map((s) => ({
                  nombre: s.name || s.nombre,
                  duracion_minutos: s.duration || s.duracion_minutos,
                  gender: s.gender || "unisex",
                })),
                unisex: unisex.map((s) => ({
                  nombre: s.name || s.nombre,
                  duracion_minutos: s.duration || s.duracion_minutos,
                  gender: s.gender || "unisex",
                })),
              },
            },
          },
        });
      }
      
      // Para otros sectores, devolver como array plano
      return respond({
        config: {
          default_services: {
            [lang]: services.map((s) => ({
              nombre: s.name || s.nombre,
              duracion_minutos: s.duration || s.duracion_minutos,
              gender: s.gender || "unisex",
            })),
          },
        },
      });
    }

    // /calendar/availability → disponibilidad general (legado)
    if (url === "/calendar/availability") {
      return respond({});
    }

    // /contactos/search → búsqueda de contactos mock (usar contactos generados por mockData)
    if (url.includes("/contactos/search")) {
      const query = (params.q || "").toLowerCase().trim();
      const sector = (state.sector || "barberia").toLowerCase();

      // Obtener contactos completos desde mockData (incluye phone real para Javier Jiménez)
      const allContacts = getContactsForSector(sector) || [];

      // Filtrar según la query si existe (buscar en nombre, teléfono o matrícula)
      const filtered = query
        ? allContacts.filter((c) => {
            const name = (c.nombre?.S || c.full_name?.S || c.nombre || "").toString().toLowerCase();
            const phone = (c.phone_number?.S || c.phone_number || "").toString().replace(/\s/g, "");
            const plateHit =
              sector === "lavadero" &&
              parseVehiclesSim(c?.vehicles?.S || c?.vehicles).some((v) =>
                String(v?.plate || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase()
                  .includes(query.replace(/[^A-Za-z0-9]/g, "").toUpperCase()),
              );
            return name.includes(query) || phone.includes(query.replace(/\s/g, "")) || plateHit;
          })
        : allContacts.slice(0, 10);

      // Formatear como contactos esperados por BookingModal (planos)
      const mockContacts = filtered.map((c, idx) => {
        const contact = {
          contact_id: (c.contact_id?.S || c.contact_id || `mock-contact-${sector}-${idx}`).toString(),
          nombre: (c.nombre?.S || c.full_name?.S || c.nombre || c.full_name || "").toString(),
          phone_number: (c.phone_number?.S || c.phone_number || "").toString(),
        };
        // Incluir genero si está disponible (para que BookingModal pueda preseleccionar)
        const gender = c?.genero?.S || c?.genero;
        if (gender) {
          contact.genero = gender;
        }
        // Incluir vehicles si está disponible (para que BookingModal auto-seleccione tamaño en lavadero)
        const vehicles = c?.vehicles?.S || c?.vehicles;
        if (vehicles) {
          contact.vehicles = vehicles;
        }
        return contact;
      });

      return respond(mockContacts);
    }

    // /calendar/bulk-cancel → simulador de cancelación masiva
    if (url.includes("/calendar/bulk-cancel")) {
      // Extraer datos tanto para GET (params) como para POST (body)
      const isPost = config.method?.toLowerCase() === "post";
      const bodyData = (params.body || config.data || {});
      
      // Soportar legacy 'date' o nuevo rango 'startDate'/'endDate'
      const actualStartDate = bodyData.startDate || params.startDate || bodyData.date || params.date;
      const actualEndDate = bodyData.endDate || params.endDate || bodyData.date || params.date;
      const actualStartTime = bodyData.startTime || params.startTime;
      const actualWorkerIds = bodyData.workerIds || params.workerIds;
      const isPreview = bodyData.preview || params.preview;

      // Siempre devolver citas de mock para la demo (una por día del rango)
      const sector = (state.sector || "barberia").toLowerCase();
      const sectorData = SECTOR_DATA_MAP[sector] || SECTOR_DATA_MAP.barberia;
      const clients = sectorData.clientNames || ["Juan Pérez", "María García"];
      const services = sectorData.services || [{ name: "Corte de pelo" }, { name: "Barba" }];

      // Generar eventos para cada día del rango
      const allMockEvents = [];
      let currentDate = dayjs(actualStartDate);
      const endDate = dayjs(actualEndDate);
      let eventCounter = 0;
      
      while (currentDate <= endDate) {
        const dateStr = currentDate.format("YYYY-MM-DD");
        // 2 eventos por día
        for (let i = 0; i < 2; i++) {
          eventCounter++;
          allMockEvents.push({
            id: `bulk-mock-${eventCounter}`,
            event_id: `bulk-mock-${eventCounter}`,
            time: actualStartTime || "10:00",
            participant_name: clients[i % clients.length],
            participant_phone: `+34600${600000 + eventCounter}`,
            service_name: services[i % services.length].name || services[i % services.length].nombre,
            worker_id: (actualWorkerIds && actualWorkerIds[0]) || (state.workers?.[0]?.worker_id || "worker-1"),
            date: dateStr
          });
        }
        currentDate = currentDate.add(1, "day");
      }

      if (isPreview) {
        return respond({ events: allMockEvents });
      } else {
        // Ejecución real (simulada): devolver éxito
        return respond({
          total: allMockEvents.length,
          cancelled: allMockEvents.length,
          failed: 0,
          results: allMockEvents.map(e => ({ id: e.id, status: "cancelled" }))
        });
      }
    }

    // ── Disponibilidad: generar fake si se usan trabajadores mock o estamos en simulador ──
    const workerIdParam = params?.worker_id;
    if (url.includes("/available-days")) {
      // Fake si no hay worker_id, si el worker es mock, o si el SERVICIO es del
      // catálogo mock (el backend real no lo conoce → 404 "Service not found").
      if (shouldFakeAvailability(params)) {
        return respond(generateFakeAvailableDays(params?.days_ahead || 30));
      }
    }
    if (url.includes("/available-slots")) {
      // Mismo criterio que en /available-days (worker mock/ausente o servicio mock)
      if (shouldFakeAvailability(params)) {
        let workersToUse = state.workers || [];
        if (workerIdParam) {
          workersToUse = workersToUse.filter(
            (w) => (w.worker_id || w.user_id || w.id) === workerIdParam,
          );
        }
        // Solo dividimos en disponibles/no-disponibles al pedir la lista completa
        // (paso Especialista). En validación de un worker concreto no se divide.
        return respond(
          generateFakeSlots(workersToUse, { withUnavailable: !workerIdParam }),
        );
      }
    }

    if (url.includes("/peluqueria/recurring-series") || url.includes("/calendar/recurring-series")) {
      const mock = getMockData(url, params, state.sector);
      return respond(mock);
    }

    // /calendar/events-by-participant → historial de citas del contacto
    if (url.includes("/calendar/events-by-participant")) {
      const mock = getMockData(url, params, state.sector);
      return respond(mock);
    }

    // /reminders → recordatorios mock
    if (url.includes("/reminders")) {
      const mock = getMockData(url, params, state.sector);
      return respond(mock);
    }

    // /peluqueria/customer-service-history → historial local del simulator
    if (url.includes("/peluqueria/customer-service-history")) {
      const sector = (state.sector || "barberia").toLowerCase();
      const sectorData = SECTOR_DATA_MAP[sector] || SECTOR_DATA_MAP.barberia;

      const isPeluqueriaSector =
        sector === "peluquerias" ||
        sector === "peluqueria_unisex";

      // Mock historial para Carmen García / Javier Jiménez (comparten vendor phone → desambiguar por contact_name)
      const CARMEN_GARCIA_CONTACT_ID = `simulator-demo#${getVendorDemoPhone()}`;
      const contactName = String(params?.contact_name || "").toLowerCase();
      const isJavierJimenez = contactName.includes("javier") && contactName.includes("jiménez");
      const isCarmenGarcia = contactName.includes("carmen") && contactName.includes("garcía");
      if (sector === "estetica" && params?.contact_id === CARMEN_GARCIA_CONTACT_ID && !isJavierJimenez) {
        const carmenEstHistory = [
          {
            service_id: "sim-est-s39",
            name: "Extensión de pestañas",
            price: 80, duration: 120, total_duration: 120, color: "#A78BFA",
            has_history: true, total_appointments: 9,
            last_appointment: "2026-03-22", recommended_duration: 120, default_duration: 120,
          },
          {
            service_id: "sim-est-s23",
            name: "Manicura semipermanente",
            price: 28, duration: 60, total_duration: 60, color: "#D97706",
            has_history: true, total_appointments: 12,
            last_appointment: "2026-03-15", recommended_duration: 60, default_duration: 60,
          },
          {
            service_id: "sim-est-s3",
            name: "Tratamiento antiedad",
            price: 80, duration: 75, total_duration: 75, color: "#DB2777",
            has_history: true, total_appointments: 4,
            last_appointment: "2026-02-28", recommended_duration: 75, default_duration: 75,
          },
        ];
        return respond({ services: carmenEstHistory });
      }
      if (isPeluqueriaSector && params?.contact_id === CARMEN_GARCIA_CONTACT_ID && isJavierJimenez) {
        const javierPeluqueriaHistory = [
          {
            service_id: "multi_sim-pel-s2_sim-pel-s9",
            name: "Corte Caballero + Lavado",
            price: 26, duration: 45, total_duration: 45, color: "#3B82F6",
            gender: "hombre", has_history: true, total_appointments: 10,
            last_appointment: "2026-03-22", recommended_duration: 45, default_duration: 45,
          },
          {
            service_id: "multi_sim-pel-s2_sim-pel-s9_sim-pel-s8",
            name: "Corte Caballero + Lavado + Tratamiento Hidratación",
            price: 56, duration: 85, total_duration: 85, color: "#06B6D4",
            gender: "hombre", has_history: true, total_appointments: 6,
            last_appointment: "2026-02-18", recommended_duration: 85, default_duration: 85,
          },
          {
            service_id: "sim-pel-s2",
            name: "Corte Caballero",
            price: 18, duration: 30, total_duration: 30, color: "#3B82F6",
            gender: "hombre", has_history: true, total_appointments: 4,
            last_appointment: "2026-01-30", recommended_duration: 30, default_duration: 30,
          },
        ];
        return respond({ services: javierPeluqueriaHistory });
      }
      if (isPeluqueriaSector && params?.contact_id === CARMEN_GARCIA_CONTACT_ID && !isJavierJimenez) {
        const carmenHistory = [
          {
            service_id: "multi_sim-pel-s4_sim-pel-s7_sim-pel-s8",
            name: "Corte y Lavado + Peinado + Tratamiento Hidratación",
            price: 83, duration: 130, total_duration: 130, color: "#EF4444",
            gender: "mujer", has_history: true, total_appointments: 9,
            last_appointment: "2026-03-14", recommended_duration: 130, default_duration: 130,
          },
          {
            service_id: "multi_sim-pel-s5_sim-pel-s1_sim-pel-s7",
            name: "Tinte + Corte Mujer + Peinado",
            price: 95, duration: 180, total_duration: 180, color: "#8B5CF6",
            gender: "mujer", has_history: true, total_appointments: 5,
            last_appointment: "2026-02-20", recommended_duration: 180, default_duration: 180,
          },
          {
            service_id: "multi_sim-pel-s6_sim-pel-s8_sim-pel-s7",
            name: "Mechas + Tratamiento Hidratación + Peinado",
            price: 120, duration: 205, total_duration: 205, color: "#EC4899",
            gender: "mujer", has_history: true, total_appointments: 3,
            last_appointment: "2026-01-15", recommended_duration: 205, default_duration: 205,
          },
        ];
        return respond({ services: carmenHistory });
      }

      // Mock historial para David Romero
      const DAVID_ROMERO_CONTACT_ID = `simulator-demo#${DAVID_ROMERO_PHONE}`;
      if (sector === "estetica" && params?.contact_id === DAVID_ROMERO_CONTACT_ID) {
        const davidEstHistory = [
          {
            service_id: "sim-est-s9",
            name: "Masaje relajante",
            price: 50, duration: 60, total_duration: 60, color: "#3B82F6",
            has_history: true, total_appointments: 8,
            last_appointment: "2026-03-18", recommended_duration: 60, default_duration: 60,
          },
          {
            service_id: "sim-est-s1",
            name: "Limpieza facial profunda",
            price: 55, duration: 60, total_duration: 60, color: "#EC4899",
            has_history: true, total_appointments: 5,
            last_appointment: "2026-03-05", recommended_duration: 60, default_duration: 60,
          },
          {
            service_id: "sim-est-s3",
            name: "Tratamiento antiedad",
            price: 80, duration: 75, total_duration: 75, color: "#DB2777",
            has_history: true, total_appointments: 2,
            last_appointment: "2026-02-10", recommended_duration: 75, default_duration: 75,
          },
        ];
        return respond({ services: davidEstHistory });
      }
      if (isPeluqueriaSector && params?.contact_id === DAVID_ROMERO_CONTACT_ID) {
        const davidHistory = [
          {
            service_id: "multi_sim-pel-s2_sim-pel-s9_sim-pel-s8",
            name: "Corte Caballero + Lavado + Tratamiento Hidratación",
            price: 56, duration: 85, total_duration: 85, color: "#3B82F6",
            gender: "hombre", has_history: true, total_appointments: 11,
            last_appointment: "2026-03-20", recommended_duration: 85, default_duration: 85,
          },
          {
            service_id: "multi_sim-pel-s2_sim-pel-s8_sim-pel-s7",
            name: "Corte Caballero + Tratamiento Hidratación + Peinado",
            price: 73, duration: 115, total_duration: 115, color: "#06B6D4",
            gender: "hombre", has_history: true, total_appointments: 8,
            last_appointment: "2026-03-10", recommended_duration: 115, default_duration: 115,
          },
          {
            service_id: "multi_sim-pel-s2_sim-pel-s9_sim-pel-s7",
            name: "Corte Caballero + Lavado + Peinado",
            price: 51, duration: 90, total_duration: 90, color: "#14B8A6",
            gender: "hombre", has_history: true, total_appointments: 4,
            last_appointment: "2026-02-05", recommended_duration: 90, default_duration: 90,
          },
        ];
        return respond({ services: davidHistory });
      }

      // Mock historial para Javier Jiménez en barbería
      const JAVIER_JIMENEZ_CONTACT_ID = `simulator-demo#${getVendorDemoPhone()}`;
      if (sector === "barberia" && params?.contact_id === JAVIER_JIMENEZ_CONTACT_ID) {
        const javierBarberiaHistory = [
          {
            service_id: "multi_sim-barb-s1_sim-barb-s5",
            name: "Corte Normal + Barba - Completa",
            price: 36, duration: 60, total_duration: 60, color: "#3B82F6",
            gender: "hombre", has_history: true, total_appointments: 12,
            last_appointment: "2026-03-24", recommended_duration: 60, default_duration: 60,
          },
          {
            service_id: "multi_sim-barb-s3_sim-barb-s6",
            name: "Corte Premium + Barba - Tratamiento Spa",
            price: 50, duration: 75, total_duration: 75, color: "#10B981",
            gender: "hombre", has_history: true, total_appointments: 8,
            last_appointment: "2026-03-10", recommended_duration: 75, default_duration: 75,
          },
          {
            service_id: "multi_sim-barb-s1_sim-barb-s7",
            name: "Corte Normal + Tinte - Camuflaje Canas",
            price: 38, duration: 60, total_duration: 60, color: "#F97316",
            gender: "hombre", has_history: true, total_appointments: 5,
            last_appointment: "2026-02-28", recommended_duration: 60, default_duration: 60,
          },
        ];
        return respond({ services: javierBarberiaHistory });
      }

      return respond({ services: sectorData.services || [] });
    }

    // ── Mock data: páginas de chats, plantillas, campañas, etc.
    const mock = getMockData(url, params, state.sector);
    if (mock !== null && mock !== undefined) {
      // Mezclar plantillas de sesión con las mock predefinidas
      if (url.includes("/plantillas") && _sessionTemplates.length > 0) {
        const base = Array.isArray(mock) ? mock : (mock.data || []);
        const merged = [..._sessionTemplates, ...base];
        return respond(Array.isArray(mock) ? merged : { ...mock, data: merged });
      }
      return respond(mock);
    }

    // ── Overrides por modelo de vehículo (lavadero by_service) ──
    // GET /services/:service_id/vehicle-overrides
    {
      const m = url.match(/^\/services\/([^/]+)\/vehicle-overrides$/);
      if (m) {
        const sid = decodeURIComponent(m[1]);
        let store = {};
        try {
          store = JSON.parse(
            localStorage.getItem("sim_service_vehicle_overrides") || "{}",
          );
        } catch (_) {
          store = {};
        }
        return respond({ items: Array.isArray(store[sid]) ? store[sid] : [] });
      }
    }

    // ── Disponibilidad con trabajador y servicio reales → backend real, con red ──
    // Si aun así el backend no conoce el servicio (404), la demo NO se queda sin
    // días: se cae a los datos fake. El gate evita el viaje en los casos conocidos;
    // esto cubre los que no anticipamos sin romper una demo delante del cliente.
    if (url.includes("/available-days") || url.includes("/available-slots")) {
      return realAxiosClient.get(url, config).catch((err) => {
        if (err?.response?.status !== 404) throw err;
        console.warn("[Simulator] disponibilidad real 404 → datos fake:", params?.service_id);
        if (url.includes("/available-days")) {
          return respond(generateFakeAvailableDays(params?.days_ahead || 30));
        }
        let workersToUse = state.workers || [];
        if (workerIdParam) {
          workersToUse = workersToUse.filter(
            (w) => (w.worker_id || w.user_id || w.id) === workerIdParam,
          );
        }
        return respond(generateFakeSlots(workersToUse, { withUnavailable: !workerIdParam }));
      });
    }

    // ── Todo lo demás → backend real ────────────────────────
    // Incluye: /contactos/search, /peluqueria/customer-service-history, etc.
    return realAxiosClient.get(url, config);
  },

  post: (url, data) => {
    const state = getState();

    // ── Lead-appointment mode: crear servicio → persistir en lead-carta ──
    // POST /peluqueria/services (sin id)  → alta nueva en /ventas/lead-carta/item.
    if (
      url === "/peluqueria/services" &&
      isLeadAppointmentMode(state)
    ) {
      const tempId = data?.service_id || null;
      const payload = {
        contact_id: state.contactId,
        item: toLeadCartaItem(data, { includeItemId: false }),
      };
      return realAxiosClient
        .post("/ventas/lead-carta/item", payload)
        .then((res) => {
          const newId = res?.item_id || tempId || `svc-${Date.now()}`;
          const saved = { ...data, service_id: newId, enabled: true };
          const currentServices = getState().services || [];
          const already = currentServices.some(
            (s) => s.service_id === (tempId || newId),
          );
          const nextServices = already
            ? currentServices.map((s) =>
                s.service_id === (tempId || newId) ? saved : s,
              )
            : [...currentServices, saved];
          dispatch({ type: "SET_SERVICES", payload: nextServices });
          return { service_id: newId, service: saved, ...saved };
        })
        .catch((err) => {
          console.error("[Simulator] lead-carta create service failed:", err);
          throw err;
        });
    }

    // Creación de cita real → backend real + actualización local del calendario
    if (url.includes("/peluqueria/appointment")) {
      // El WhatsApp de la demo debe llegar a CUALQUIER teléfono escrito a mano
      // (p.ej. el del lead), no solo a los contactos demo predefinidos. Para un
      // contacto demo el WhatsApp se entrega en el número del propio vendedor
      // (getDemoPhoneMap); para un contacto custom se entrega en el teléfono
      // escrito. En ambos casos la cita sale por v_simulatorBookDemo (número
      // trial del sector) en vez del backend real /peluqueria/appointment, que
      // rechaza con 404 los service_id mock del simulador ("sim-*") y dejaba la
      // cita sin enviar WhatsApp (mostrando un éxito falso por el fallback local).
      const mappedDemoPhone = data?.customer_phone && getDemoPhoneMap()[data.customer_phone];
      const whatsappPhone = mappedDemoPhone || data?.customer_phone;
      const isDemoContact = !!whatsappPhone && !url.includes("recurring");

      if (isDemoContact) {
        const services = getState().services || [];
        const workers  = getState().workers  || [];
        const sector = (data?.sector || getState().sector || "peluqueria").toLowerCase();
        return realAxiosClient
          .post("/ventas/simulator-book-demo", {
            customer_name:  data.customer_name,
            customer_phone: data.customer_phone,
            whatsapp_phone: whatsappPhone, // teléfono real donde llega el WhatsApp
            service_id:     data.service_id,
            service_name:   data.service_name || services.find((s) => s.service_id === data.service_id)?.name || "",
            worker_id:      data.worker_id,
            worker_name:    workers.find((w) => (w.worker_id || w.user_id) === data.worker_id)?.name || "",
            sector,
            // Nombre del negocio para el {{1}} de la plantilla _trial: el del lead
            // si la demo se abrió desde un lead; null → el backend usa un genérico
            // por sector ("Lavadero ConextCRM", etc.).
            business_name:  getState().contactName || null,
          })
          .then((result) => {
            const eventId   = result?.event_id || `apt-demo-${Date.now()}`;
            const demoDate  = result?.date      || data.date;
            const demoTime  = result?.start_time || "10:00";
            dispatch({
              type: "ADD_APPOINTMENT",
              payload: {
                event_id:       eventId,
                id:             eventId,
                service_name:   result?.service_name || data.service_name || "",
                worker_name:    result?.worker_name  || data.worker_name  || "",
                worker_id:      data.worker_id,
                service_id:     data.service_id,
                customer_name:  data.customer_name,
                customer_phone: data.customer_phone,
                start:          `${demoDate}T${demoTime}:00`,
                end:            null,
                date:           demoDate,
                start_time:     demoTime,
                origin_kind:    "booking",
                creation_source: "crm",
              },
            });
            // Recordatorio a los 5s DESACTIVADO: en la demo solo llega la
            // confirmación de la cita. Se conserva comentado por si se quiere
            // reactivar el envío del recordatoriop1 al mismo número.
            // const _reminderDate = result?.date      || data.date;
            // const _reminderTime = result?.start_time || "10:00";
            // setTimeout(() => {
            //   realAxiosClient
            //     .post("/ventas/simulator-send-reminder", {
            //       customer_name:    data.customer_name,
            //       customer_phone:   data.customer_phone,
            //       whatsapp_phone:   whatsappPhone,
            //       appointment_date: _reminderDate,
            //       appointment_time: _reminderTime,
            //       sector,
            //       business_name:    getState().contactName || null,
            //     })
            //     .catch((err) =>
            //       console.warn("[Simulator] v_simulatorSendReminder failed:", err?.message)
            //     );
            // }, 5000);

            // (Eliminado) El interactive list de "packs habituales" / "Elige el
            // servicio" tras la cita: en la demo solo deben llegar la confirmación
            // y el recordatorio.

            return result;
          })
          .catch((error) => {
            // Fallback local: el demo no debe romperse si la lambda falla
            console.warn("[Simulator] v_simulatorBookDemo failed:", error?.message);
            const eventId = `apt-demo-${Date.now()}`;
            dispatch({
              type: "ADD_APPOINTMENT",
              payload: {
                event_id: eventId,
                id: eventId,
                ...data,
                start: `${data.date}T${data.start_time}:00`,
                origin_kind: "booking",
                _local_only: true,
              },
            });
            return { success: true, event_id: eventId, _local_only: true };
          });
      }

      return realAxiosClient
        .post(url, data)
        .then((result) => {
          // Agregar la cita al calendario en memoria para visualización inmediata
          const eventId =
            result?.event_id || result?.id || `apt-${Date.now()}`;
          const serviceName =
            (state.services || []).find(
              (s) => s.service_id === data.service_id,
            )?.name || data.service_name || "";
          const workerName =
            (state.workers || []).find(
              (w) =>
                (w.worker_id || w.user_id) === data.worker_id,
            )?.name || data.worker_name || "";

          dispatch({
            type: "ADD_APPOINTMENT",
            payload: {
              event_id: eventId,
              id: eventId,
              service_name: serviceName,
              worker_name: workerName,
              worker_id: data.worker_id,
              customer_name: data.customer_name,
              customer_phone: data.customer_phone,
              service_id: data.service_id,
              start: `${data.date}T${data.start_time}:00`,
              end: null,
              date: data.date,
              start_time: data.start_time,
              origin_kind: "booking",
              creation_source: "crm",
            },
          });

          return result;
        })
        .catch((error) => {
          // Si el backend falla (ej: service_id no existe en p_services),
          // caemos a un estado local simulado para que la demo continúe.
          console.warn(
            "[Simulator] Fallo al crear cita en backend real, usando estado local:",
            error?.message || error,
          );
          const eventId = `apt-${Date.now()}`;
          dispatch({
            type: "ADD_APPOINTMENT",
            payload: {
              event_id: eventId,
              id: eventId,
              ...data,
              start: `${data.date}T${data.start_time}:00`,
              origin_kind: "booking",
              _local_only: true, // flag informativo
            },
          });
          // Devolver respuesta de éxito simulada para que el BookingModal muestre el paso 6 (éxito)
          return {
            success: true,
            event_id: eventId,
            reminder: null,
            _local_only: true,
          };
        });
    }

    // Actualización de contacto en simulador → respuesta OK sin persistencia real
    if (url === "/contactos/update") {
      return respond({ success: true });
    }

    // Creación de contacto → backend real
    if (url.startsWith("/contactos")) {
      return realAxiosClient.post(url, data);
    }

    // Actualización de trabajador → persistir cambios de nombre/teléfono en state.workers
    // para que el GET /usuarios devuelva los datos actualizados al reabrir el modal.
    if (url === "/usuarios/update") {
      const uid = data?.user_id;
      if (uid) {
        const newName =
          data.full_name || data.username || undefined;
        const changes = {};
        if (newName) {
          changes.name = newName;
          changes.username = newName;
          changes.displayName = newName;
        }
        if (data.phone !== undefined) changes.phone = data.phone;
        if (data.level !== undefined) changes.level = data.level;
        if (Object.keys(changes).length > 0) {
          dispatch({ type: "UPDATE_WORKER", payload: { user_id: uid, ...changes } });
        }
      }
      return respond({ success: true });
    }

    // Alta de trabajador → persistir en memoria (state.workers) para que el
    // GET /usuarios posterior lo muestre en pantalla durante la sesión del
    // simulador. Se limpia al salir del simulador (DEACTIVATE).
    if (url === "/usuarios" && data?.new_user_id) {
      const uid = data.new_user_id;
      const name = data.username || data.full_name || uid;
      dispatch({
        type: "ADD_WORKER",
        payload: {
          user_id: uid,
          worker_id: uid,
          name,
          username: name,
          displayName: name,
          level: data.level ?? 1,
          department_id: data.department_id || "dept-default",
        },
      });
      return respond({ success: true });
    }

    // Borrado de trabajador → quitar de memoria.
    if (url === "/usuarios/delete") {
      const uid = data?.user_id;
      if (uid) dispatch({ type: "DELETE_WORKER", payload: { user_id: uid } });
      return respond({ success: true });
    }

    // Operaciones de empresa/usuarios en el simulador → respuesta OK sin persistencia
    if (
      url.startsWith("/departamentos") ||
      url.startsWith("/usuarios") ||
      url.includes("/company/settings") ||
      url.includes("/modifyUserDept") ||
      url.includes("/stripe")
    ) {
      return respond({ success: true });
    }

    // Personalización de servicio por contacto → backend real (para demo de customización)
    if (url.includes("/peluqueria/service-customization")) {
      return realAxiosClient.post(url, data);
    }

    // Acciones de recordatorios y series recurrentes en simulador → respuesta OK
    if (
      url.includes("/reminders") || 
      url.includes("/recurring-series") || 
      url.includes("/contact-reminder")
    ) {
      return respond({ success: true, reminders: [] });
    }

    // /calendar/bulk-cancel → simulador de ejecución de cancelación masiva
    if (url.includes("/calendar/bulk-cancel")) {
      return respond({
        total: 2,
        cancelled: 2,
        failed: 0,
        results: [{ id: "bulk-mock-1", status: "cancelled" }, { id: "bulk-mock-2", status: "cancelled" }]
      });
    }

    // Fallback DELETE-via-POST cuando un ad-blocker bloquea DELETE
    if (url === "/calendar/event-delete") {
      const eventId = data?.event_id;
      if (eventId) dispatch({ type: "DELETE_APPOINTMENT", payload: eventId });
      return respond({ success: true });
    }

    // Creación de evento via endpoint singular (/calendar/event sin event_id en body)
    if (url === "/calendar/event" || url.startsWith("/calendar/event?")) {
      const newId = data?.event_id || `apt-manual-${Date.now()}`;
      dispatch({
        type: "ADD_APPOINTMENT",
        payload: {
          ...data,
          event_id: newId,
          id: newId,
          origin_kind: data?.origin_kind || "manual",
          creation_source: "crm",
        },
      });
      return respond({ success: true, event_id: newId, message: "Evento creado" });
    }

    // Creación de plantilla en simulador → mock sin backend
    if (url === "/plantillas" || url.startsWith("/plantillas?")) {
      const body = data instanceof FormData
        ? JSON.parse(data.get("template") || "{}")
        : (typeof data === "string" ? JSON.parse(data) : data);
      const mockTpl = {
        id: `mock_tpl_${Date.now()}`,
        name: body?.name || "plantilla_demo",
        status: "PENDING",
        language: body?.language || "es",
        category: body?.category || "MARKETING",
        components: body?.components || [],
        created_at: new Date().toISOString(),
      };
      _sessionTemplates.push(mockTpl);
      return new Promise((res) => setTimeout(() => res(mockTpl), 600));
    }

    // Creación de campaña en simulador → mock sin backend
    if (url === "/campaigns" || url.startsWith("/campaigns?")) {
      const body = typeof data === "string" ? JSON.parse(data) : data;
      const mockCampaign = {
        id: `mock_campaign_${Date.now()}`,
        ...body,
        status: "Pendiente",
        phases: (body?.sendPhases || []).length || 1,
        created_at: new Date().toISOString(),
      };
      return new Promise((res) => setTimeout(() => res({ campaign: mockCampaign, phases: mockCampaign.phases }), 800));
    }

    // Cualquier otra llamada POST → backend real
    return realAxiosClient.post(url, data);
  },

  put: (url, data) => {
    const state = getState();

    // Edición de evento del calendario → solo estado local
    if (url.match(/^\/(peluqueria\/)?events\//)) {
      const id = url.split(/\/events\//)[1];
      dispatch({ type: "UPDATE_APPOINTMENT", payload: { id, changes: data } });
      return respond({ success: true });
    }

    // Edición de evento via endpoint singular (/calendar/event con event_id en body)
    if (url === "/calendar/event" || url.startsWith("/calendar/event?")) {
      const eventId = data?.event_id;
      if (eventId) {
        dispatch({
          type: "UPDATE_APPOINTMENT",
          payload: { id: eventId, changes: data },
        });
      }
      return respond({ success: true, event_id: eventId, message: "Evento actualizado" });
    }

    // ── Lead-appointment mode: editar/guardar catálogo → lead-carta ──
    // PUT /peluqueria/services/:id → upsert en /ventas/lead-carta/item.
    const singleId = extractServiceId(url);
    if (
      singleId &&
      url.startsWith("/peluqueria/services/") &&
      isLeadAppointmentMode(state)
    ) {
      // Solo persistir si el ID es un UUID real; los IDs mock (sim-pel-s3, etc.)
      // no deben guardarse en DynamoDB directamente para evitar duplicados.
      if (!isUuidLike(singleId)) {
        return Promise.resolve({ success: true });
      }
      const payload = {
        contact_id: state.contactId,
        item: {
          ...toLeadCartaItem(data, { includeItemId: false }),
          item_id: singleId,
        },
      };
      return realAxiosClient
        .post("/ventas/lead-carta/item", payload)
        .then(() => {
          const currentServices = getState().services || [];
          dispatch({
            type: "SET_SERVICES",
            payload: currentServices.map((s) =>
              s.service_id === singleId ? { ...s, ...data, service_id: singleId } : s,
            ),
          });
          return { success: true };
        })
        .catch((err) => {
          console.error("[Simulator] lead-carta update service failed:", err);
          throw err;
        });
    }

    // PUT /peluqueria/services (bulk save final) en lead-appointment:
    //   1. Reconcile deletions (items previos no presentes en la nueva lista).
    //   2. Upsert cada servicio: slug => alta, uuid => update.
    //   3. Actualizar service_id de los nuevos con el item_id devuelto.
    if (
      url === "/peluqueria/services" &&
      isLeadAppointmentMode(state) &&
      data &&
      Array.isArray(data.services)
    ) {
      const prevServices = state.services || [];
      const newList = data.services;
      const keepUuids = new Set(
        newList.map((s) => s.service_id).filter((id) => isUuidLike(id)),
      );
      const toDelete = prevServices.filter(
        (s) => isUuidLike(s.service_id) && !keepUuids.has(s.service_id),
      );

      const run = async () => {
        for (const s of toDelete) {
          try {
            await realAxiosClient.delete("/ventas/lead-carta/item", {
              data: { contact_id: state.contactId, item_id: s.service_id },
            });
          } catch (err) {
            console.warn("[Simulator] lead-carta delete during bulk save failed:", err);
          }
        }
        const persisted = [];
        for (const svc of newList) {
          const includeItemId = isUuidLike(svc.service_id);
          try {
            const res = await realAxiosClient.post("/ventas/lead-carta/item", {
              contact_id: state.contactId,
              item: toLeadCartaItem(svc, { includeItemId }),
            });
            const finalId = res?.item_id || (includeItemId ? svc.service_id : `svc-${Date.now()}-${Math.random().toString(16).slice(2)}`);
            persisted.push({ ...svc, service_id: finalId });
          } catch (err) {
            console.warn("[Simulator] lead-carta upsert during bulk save failed:", err);
            persisted.push(svc);
          }
        }
        dispatch({ type: "SET_SERVICES", payload: persisted });
        return { success: true, services: persisted };
      };
      return run();
    }

    // Edición de servicios → solo estado local (modo simulador sin lead)
    if (isServicesUrl(url)) {
      const servicesPayload = Array.isArray(data)
        ? data
        : Array.isArray(data?.services)
          ? data.services
          : [];
      dispatch({ type: "SET_SERVICES", payload: servicesPayload });
      return respond({ success: true });
    }

    // Asignaciones de servicio a trabajadores → cachear el body PUT para que
    // el siguiente GET devuelva exactamente lo que se acaba de enviar.
    if (url.includes("/peluqueria/service-assignments")) {
      const incoming = Array.isArray(data?.assignments)
        ? data.assignments
        : Array.isArray(data)
          ? data
          : null;
      if (incoming) {
        _cachedServiceAssignments = incoming
          .filter((a) => a && a.service_id && a.user_id)
          .map((a) => ({ service_id: a.service_id, user_id: a.user_id }));
      }
      return respond({ success: true });
    }

    // Horario de trabajador → persistir en memoria (schedule + periodos especiales)
    // para que el siguiente GET devuelva exactamente lo guardado (demo del round-trip).
    if (url.includes("/peluqueria/user-schedule")) {
      const body = typeof data === "string" ? JSON.parse(data) : (data || {});
      if (isAutoescuelaState(getState()) && isProfesorId(body.target_user_id)) {
        setProfesorSchedule(body.target_user_id, body.schedule, body.schedule_overrides);
        return respond({ success: true });
      }
      const uid = body.target_user_id || "__self__";
      _mockSchedulesByUser[uid] = {
        schedule: body.schedule || {},
        schedule_overrides: Array.isArray(body.schedule_overrides)
          ? body.schedule_overrides
          : (_mockSchedulesByUser[uid]?.schedule_overrides || []),
      };
      return respond({ success: true });
    }

    // Disponibilidad de calendario (legado) → solo estado local
    if (url === "/calendar/availability") {
      return respond({ success: true });
    }

    // Ajustes de empresa → solo estado local en simulador
    if (url.includes("/company/settings")) {
      return respond({ success: true });
    }

    // Food-orders mutations → no-op en simulador
    if (
      url.startsWith("/pedidos") ||
      url.startsWith("/carta") ||
      url.startsWith("/reservas") ||
      url.startsWith("/menu-dia")
    ) {
      return respond({ success: true });
    }

    // ── Overrides por modelo de vehículo (lavadero by_service) ──
    // PUT /services/:service_id/vehicle-overrides
    {
      const m = url.match(/^\/services\/([^/]+)\/vehicle-overrides$/);
      if (m) {
        const sid = decodeURIComponent(m[1]);
        let store = {};
        try {
          store = JSON.parse(
            localStorage.getItem("sim_service_vehicle_overrides") || "{}",
          );
        } catch (_) {
          store = {};
        }
        const list = Array.isArray(store[sid]) ? store[sid] : [];
        const brand = String(data?.brand || "").trim().toUpperCase();
        const model = String(data?.model || "").trim();
        const price = data?.price ?? null;
        const duration = data?.duration ?? null;
        const item = { brand, model, price, duration };
        const idx = list.findIndex(
          (o) => o.brand === brand && o.model === model,
        );
        if (idx >= 0) list[idx] = item;
        else list.push(item);
        store[sid] = list;
        try {
          localStorage.setItem(
            "sim_service_vehicle_overrides",
            JSON.stringify(store),
          );
        } catch (_) {}
        return respond({ ok: true, ...item });
      }
    }

    // Todo lo demás → backend real
    return realAxiosClient.put(url, data);
  },

  delete: (url, config = {}) => {
    const state = getState();

    // Eliminación de evento → solo estado local
    if (url.match(/^\/(peluqueria\/|calendar\/)?events\//)) {
      const id = url.split(/\/events\//)[1];
      dispatch({ type: "DELETE_APPOINTMENT", payload: id });
      return respond({ success: true });
    }

    // Eliminación de evento via endpoint singular (/calendar/event?event_id=...)
    if (url === "/calendar/event" || url.startsWith("/calendar/event?")) {
      const qs = url.includes("?") ? url.split("?")[1] : "";
      const params = {
        ...Object.fromEntries(new URLSearchParams(qs)),
        ...(config?.params || {}),
      };
      const eventId = params.event_id;
      if (eventId) {
        dispatch({ type: "DELETE_APPOINTMENT", payload: eventId });
      }
      return respond({ success: true });
    }


    // ── Lead-appointment mode: borrar servicio(s) del catálogo ──
    // DELETE /peluqueria/services/:id → /ventas/lead-carta/item.
    const singleId = extractServiceId(url);
    if (
      singleId &&
      url.startsWith("/peluqueria/services/") &&
      isLeadAppointmentMode(state)
    ) {
      const finish = () => {
        const currentServices = getState().services || [];
        dispatch({
          type: "SET_SERVICES",
          payload: currentServices.filter((s) => s.service_id !== singleId),
        });
        return { success: true };
      };
      if (!isUuidLike(singleId)) {
        // Aún no estaba persistido en backend (slug de sugerencia) → solo state.
        return respond(finish());
      }
      return realAxiosClient
        .delete("/ventas/lead-carta/item", {
          data: { contact_id: state.contactId, item_id: singleId },
        })
        .then(finish)
        .catch((err) => {
          console.error("[Simulator] lead-carta delete service failed:", err);
          throw err;
        });
    }

    // DELETE /peluqueria/services?gender=... → bulk por bucket de género.
    if (
      url.startsWith("/peluqueria/services") &&
      isLeadAppointmentMode(state)
    ) {
      const qs = url.includes("?") ? url.split("?")[1] : "";
      const params = {
        ...Object.fromEntries(new URLSearchParams(qs)),
        ...(config?.params || {}),
      };
      const bucket = (params.gender || "").toLowerCase();
      const all = state.services || [];
      const toKeep = all.filter((s) => !matchesGenderBucket(s.gender, bucket));
      const toDelete = all.filter((s) => matchesGenderBucket(s.gender, bucket));

      const run = async () => {
        for (const s of toDelete) {
          if (!isUuidLike(s.service_id)) continue;
          try {
            await realAxiosClient.delete("/ventas/lead-carta/item", {
              data: { contact_id: state.contactId, item_id: s.service_id },
            });
          } catch (err) {
            console.warn("[Simulator] lead-carta bulk delete failed:", err);
          }
        }
        dispatch({ type: "SET_SERVICES", payload: toKeep });
        return { success: true };
      };
      return run();
    }

    // Eliminación de recordatorios → solo simulador
    if (url.includes("/reminders")) {
      return respond({ success: true });
    }

    // ── Overrides por modelo de vehículo (lavadero by_service) ──
    // DELETE /services/:service_id/vehicle-overrides/:vehicle_key
    {
      const m = url.match(
        /^\/services\/([^/]+)\/vehicle-overrides\/([^/]+)$/,
      );
      if (m) {
        const sid = decodeURIComponent(m[1]);
        const key = decodeURIComponent(m[2]);
        let store = {};
        try {
          store = JSON.parse(
            localStorage.getItem("sim_service_vehicle_overrides") || "{}",
          );
        } catch (_) {
          store = {};
        }
        const list = Array.isArray(store[sid]) ? store[sid] : [];
        store[sid] = list.filter((o) => `${o.brand}::${o.model}` !== key);
        try {
          localStorage.setItem(
            "sim_service_vehicle_overrides",
            JSON.stringify(store),
          );
        } catch (_) {}
        return respond({ ok: true });
      }
    }

    // Todo lo demás → backend real
    return realAxiosClient.delete(url, config);
  },

  patch: (url, data) => realAxiosClient.patch(url, data),

  // Stub para interceptors (algunos componentes llaman axiosClient.interceptors)
  interceptors: {
    request: { use: () => {} },
    response: { use: () => {} },
  },
};

export default axiosClient;
